<template>
    <v-dialog v-model="open" max-width="500px" persistent>
      <template v-slot:activator="{ on, attrs }">
        <location-info-item
          icon="mdi-file-tree"
          icon-action="mdi-pencil-outline"
          title="Categorias"
          :subtitle="getSubtitle()"
          v-bind="attrs"
          v-on="on"
        />
      </template>
  
      <v-card>
        <v-toolbar elevation="0">
          <v-card-title class="ml-0 pl-1">
            Categorias
          </v-card-title>
  
          <v-spacer />
  
          <v-btn icon @click="open = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
  
        <v-card-text>
          Aqui você pode alterar a categoria principal e as categorias adicionais da empresa.
        </v-card-text>
  
        <v-card-text>
          <v-form ref="form" v-model="formValid" lazy-validation @submit.prevent>
            <v-autocomplete
              v-model="primaryCategoryId"
              :search-input.sync="primarySearch"
              :items="categoryOptions"
              item-text="display_name"
              item-value="category_id"
              label="Procurar e selecionar categoria primária"
              clearable
              class="mb-3"
            />
  
            <v-autocomplete
              v-model="selectedCategories"
              :search-input.sync="searchCategory"
              :items="categoryOptions"
              item-text="display_name"
              item-value="id"
              menu-props="auto"
              label="Procurar e selecionar categorias adicionais"
              multiple
              chips
              clearable
            />
  
            <v-alert
              v-if="alertScheduledTask"
              dense
              border="right"
              type="warning"
            >
              Existe um agendamento pendente para essa informação
            </v-alert>
  
            <schedule-form v-if="!bulkUpdate" small v-on:change="changeScheduleDate" />
          </v-form>
        </v-card-text>
  
        <v-divider />
  
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="cancel">
            Cancelar
          </v-btn>
          <v-btn :loading="loading" color="primary" text @click="callAction">
            Aplicar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>
  
  <script>
  import LocationInfoItem from '@/views/Locations/Components/LocationInfoItem';
  import { mapState } from 'vuex';
  import { get } from 'lodash';
  import { debounce } from 'lodash';
  import ScheduleForm from '@/views/Locations/Components/ScheduleForm';
  import { TYPE_UPDATE_LOCATION_CATEGORIES } from '@/constants/tasks';
  
  export default {
    name: 'LocationCategoriesModal',
    props: {
      action: Function,
      bulkUpdate: {
        type: Boolean,
        default: false
      },
      alertScheduleTask: {
        type: Array
      }
    },
    components: {
      LocationInfoItem,
      ScheduleForm
    },
    computed: {
      ...mapState({
        location: (state) => state.locations.location
      }),
    },
    data() {
      return {
        open: false,
        loading: false,
        formValid: true,
        formData: {
          schedule_type: TYPE_UPDATE_LOCATION_CATEGORIES,
          schedule_start: '',
          schedule_start_at: '',
          schedule_end: '',
          schedule_end_at: ''
        },
        primaryCategoryId: null,
        selectedCategories: [],
        categoryOptions: [],
        primarySearch: '',
        searchCategory: '',
        loadingCategories: false,
        alertScheduledTask: null,
      };
    },
    mounted() {
        if(this.alertScheduleTask) {
            this.alertScheduleTask.map(type => {
                if(type === TYPE_UPDATE_LOCATION_CATEGORIES) {
                this.alertScheduledTask = true;
                }
            })
        }
      this.initializeData();
    },
    watch: {
      location(location) {
        this.initializeData(location);
      },
      primarySearch(search) {
        if (search) {
          this.onCategoryKeyUp(search);
        }
      },
      searchCategory(search) {
        if (search) {
          this.onCategoryKeyUp(search);
        }
      },
      open(val) {
        if (val === true) {
          this.initializeData();
        }
      }
    },
    methods: {
      initializeData() {
        if (!this.bulkUpdate && this.location && this.location.data) {
          const locData = this.location.data;
          this.primaryCategoryId = locData.primary_category ? locData.primary_category.category_id : null;
          this.selectedCategories = (locData.categories || []).map(cat => cat.id);
        }
      },
      getSubtitle() {
        if (this.bulkUpdate) {
          return this.selectedCategories.length ? `Principais categorias selecionadas: ${this.selectedCategories.length}` : 'Nenhuma categoria adicional selecionada';
        } else if (this.location && this.location.data) {
          const primaryCat = get(this.location, 'data.primary_category.display_name', 'Sem categoria primária');
          return `Primária: ${primaryCat} / Adicionais: ${ (this.location.data.categories || []).length }`;
        }
        return 'Sem dados';
      },
      cancel: function() {
        this.open = false;
        this.resetData();
      },
      callAction: async function() {
        if (!this.validate()) {
          return;
        }
  
        const payload = {
          primary_category_id: this.primaryCategoryId || null,
          categories: this.selectedCategories || [],
          schedule_start: this.formData.schedule_start,
          schedule_start_at: this.formData.schedule_start_at,
          schedule_end: this.formData.schedule_end,
          schedule_end_at: this.formData.schedule_end_at
        };
  
        if (this.bulkUpdate) {
          // Emit the payload to be handled outside
          this.$emit('savePayload', 'primary_category_id', payload.primary_category_id);
          this.$emit('savePayload', 'categories', payload.categories);
        } else if (this.action) {
          this.loading = true;
          await this.action(payload);
          this.loading = false;
        }
        this.open = false;
      },
      validate: function() {
        return this.$refs.form.validate();
      },
      changeScheduleDate: function(data) {
        this.formData.schedule_start = data.schedule_start;
        this.formData.schedule_start_at = data.schedule_start_at;
        this.formData.schedule_end = data.schedule_end;
        this.formData.schedule_end_at = data.schedule_end_at;
      },
      resetData: function() {
        if (!this.bulkUpdate && this.location && this.location.data) {
          this.primaryCategoryId = this.location.data.primary_category ? this.location.data.primary_category.category_id : null;
          this.selectedCategories = (this.location.data.categories || []).map(cat => cat.id);
        } else {
          this.primaryCategoryId = null;
          this.selectedCategories = [];
        }
      },
      async fetchCategories(search) {
        this.loadingCategories = true;
        try {
          const response = await this.$store.dispatch('locations/getCategories', {
            limit: 10,
            search: search,
          });
          console.log(response);
          this.categoryOptions = response.data || [];
        } catch (e) {
            console.error(e);
        } finally {
          this.loadingCategories = false;
        }
      },
      onCategoryKeyUp: debounce(function (search) {
        this.fetchCategories(search);
      }, 500),
    }
  };
  </script>
  
  <style scoped></style>
  